import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '@view/shared/molecules/modal/modal.component';
import { TraceabilityPresenter } from '@view/preclinical/presenters/traceability.presenter';
import {
  CaseListItemDTO,
  VersionHistoryEntry
} from '@infra-services/traceability/api/traceability-api.interface';

@Component({
  selector: 'app-version-history-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent],
  templateUrl: './version-history-modal.component.html',
  styleUrls: ['./version-history-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VersionHistoryModalComponent {
  // Inputs usando signals API
  readonly caseItem = input.required<CaseListItemDTO>();
  readonly historyEntries = input<VersionHistoryEntry[]>([]);
  readonly isLoadingHistory = input<boolean>(false);
  readonly error = input<string | null>(null);

  // Output usando signals API
  readonly closeModal = output<void>();

  private readonly presenter = inject(TraceabilityPresenter);

  // Aliases para el template
  readonly historyVersions = this.historyEntries;
  readonly isLoading = this.isLoadingHistory;

  collapsedVersions = new Set<number>();

  onClose(): void {
    this.closeModal.emit();
  }

  toggleCollapse(index: number): void {
    if (this.collapsedVersions.has(index)) {
      this.collapsedVersions.delete(index);
    } else {
      this.collapsedVersions.add(index);
    }
  }

  getPatientFullName(): string {
    const firstName = this.caseItem().patientFirstName || '';
    const lastName = this.caseItem().patientLastName || '';
    return `${firstName} ${lastName}`.trim() || '-';
  }

  getEventTypeLabel(eventType: string): string {
    const labels: Record<string, string> = {
      CREATED: 'Creado',
      UPDATED: 'Actualizado',
      STATUS_CHANGED: 'Cambio de Estado',
      TRANSACTION: 'Transacción',
      SENT: 'Enviado',
      VALIDATED: 'Validado',
      DELETED: 'Eliminado'
    };
    return labels[eventType] || eventType || 'Registro';
  }

  getStatusFromChanges(
    changes: {
      field: string;
      oldValue: string | null;
      newValue: string | null;
    }[],
    version?: VersionHistoryEntry
  ): string {
    if (version?.status) {
      return version.status;
    }

    const statusChange = changes.find((c) => c.field === 'Estado');
    if (statusChange?.newValue) {
      return statusChange.newValue;
    }

    const actionChange = changes.find((c) => c.field === 'Acción');
    if (actionChange?.newValue) {
      const actionLower = actionChange.newValue.toLowerCase();

      if (
        actionLower.includes('guardado definitivo') ||
        actionLower.includes('laboratorio')
      ) {
        return 'Pendiente Extracción';
      }
      if (
        actionLower.includes('ingreso manual') ||
        actionLower.includes('recibido') ||
        actionLower.includes('registrad')
      ) {
        return 'Recibido';
      }
      if (actionLower.includes('extracción')) {
        return 'Pendiente Extracción';
      }
    }
    return 'Sin Estado';
  }
}
